import { calculateQuote, type PriceCatalogVersion, type RequirementDraft, type QuoteLine } from '@quotation/domain';

const CATALOG_KEY = 'quotation.catalog.v1';
const DRAFT_KEY = 'quotation.offlineDrafts.v1';

async function desktopDb() {
  if (!('__TAURI_INTERNALS__' in window)) return undefined;
  const { default: Database } = await import('@tauri-apps/plugin-sql');
  const db = await Database.load('sqlite:quotation-offline.db');
  await db.execute(`
    CREATE TABLE IF NOT EXISTS offline_cache (
      cache_key TEXT PRIMARY KEY,
      payload TEXT NOT NULL,
      updated_at TEXT NOT NULL
    )
  `);
  return db;
}

async function setCache(key: string, value: unknown): Promise<void> {
  const serialized = JSON.stringify(value);
  const db = await desktopDb();
  if (db) {
    await db.execute(
      'INSERT INTO offline_cache(cache_key, payload, updated_at) VALUES ($1, $2, $3) ON CONFLICT(cache_key) DO UPDATE SET payload = excluded.payload, updated_at = excluded.updated_at',
      [key, serialized, new Date().toISOString()],
    );
    return;
  }
  localStorage.setItem(key, serialized);
}

async function getCache<T>(key: string): Promise<T | undefined> {
  const db = await desktopDb();
  if (db) {
    const rows = await db.select<Array<{ payload: string }>>('SELECT payload FROM offline_cache WHERE cache_key = $1', [key]);
    return rows[0] ? JSON.parse(rows[0].payload) as T : undefined;
  }
  const value = localStorage.getItem(key);
  return value ? JSON.parse(value) as T : undefined;
}

export async function cachePublishedCatalog(catalog: PriceCatalogVersion): Promise<void> {
  await setCache(CATALOG_KEY, catalog);
}

export async function getCachedCatalog(): Promise<PriceCatalogVersion | undefined> {
  return getCache<PriceCatalogVersion>(CATALOG_KEY);
}

export async function cacheOfflineDraft(requirement: RequirementDraft): Promise<void> {
  const drafts = await getCache<RequirementDraft[]>(DRAFT_KEY) ?? [];
  await setCache(DRAFT_KEY, [requirement, ...drafts.filter((draft) => draft.id !== requirement.id)].slice(0, 100));
}

export async function getOfflineDrafts(): Promise<RequirementDraft[]> {
  return await getCache<RequirementDraft[]>(DRAFT_KEY) ?? [];
}

export async function calculateOfflineQuote(requirement: RequirementDraft): Promise<{ lines: QuoteLine[]; totalMinor: number; warning?: string }> {
  const catalog = await getCachedCatalog();
  if (!catalog) {
    return { lines: [], totalMinor: 0, warning: '本机还没有已同步的价格快照，无法离线核价。' };
  }
  const result = calculateQuote({ requirement, catalog });
  return {
    lines: result.lines,
    totalMinor: result.totalMinor,
    warning: result.unresolvedQuestions[0] ?? '离线结果仅用于内部草案，联网后仍需重新计算并提交审批。',
  };
}
